/**
 * Menu do usuário: identidade, preferências e o acesso à Administração.
 *
 * POR QUE A ADMINISTRAÇÃO SAIU DO MENU LATERAL
 *
 * O menu lateral é do trabalho de todo dia. "Pipelines", "Usuários e perfis",
 * "Regras comerciais", "Auditoria" e "Dados de demonstração" são coisas que se
 * mexe uma vez por trimestre — e ocupavam metade da altura visível, competindo
 * com Empresas, Oportunidades e Atividades, que se usa toda hora.
 *
 * Além do ruído, havia um efeito pior: um menu cheio de configuração faz o CRM
 * PARECER uma ferramenta de configuração. Quem entra pela primeira vez lê a
 * navegação para entender o produto.
 *
 * O que NÃO muda: nenhuma função administrativa foi removida ou perdida. Todas
 * continuam nas mesmas rotas, com as mesmas permissões, alcançáveis por aqui e
 * pela página /admin.
 *
 * ESCONDER NÃO É PROTEGER
 *
 * `can()` decide o que aparece; quem protege é o backend, que revalida cada rota
 * administrativa. Este menu não mostra "Administração" para quem não pode — mas
 * digitar a URL na barra também não funciona, e é isso que conta.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconBuilding, IconChevronDown, IconLogout, IconSettings, IconShield, IconSpark, IconUsers } from './ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useConfiguracaoPublica } from '../lib/configuracao-publica';
import { t } from '../lib/i18n';

function iniciais(nome: string): string {
  return nome
    .replace(/\[DEMO\]\s*/, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

/** Permissões que dão acesso a alguma área administrativa. */
export const PERMISSOES_ADMIN = [
  'pipeline:configure',
  'user:manage',
  'policy:read',
  'audit:read',
  'demo:manage',
  // Estas duas faltavam: quem tem só uma delas administra alguma coisa e não
  // alcançava /admin — a página existia e o caminho até ela, não.
  'feature:manage',
  'event:read',
];

interface Props {
  aoAbrirGuia: () => void;
  /** Menu lateral recolhido: mostra só o avatar, sem nome/empresa/seta. */
  colapsado?: boolean;
}

export function MenuDoUsuario({ aoAbrirGuia, colapsado = false }: Props): JSX.Element {
  const { user, logout, canAny, trocarOrganizacao, entrarNaPlataforma } = useSession();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const podeAdministrar = canAny(PERMISSOES_ADMIN);
  const cadastroAberto = useConfiguracaoPublica().config.cadastroPublicoHabilitado;
  // CERES-ADM-003: convites de outras organizações esperando a pessoa aceitar.
  const convites = useQuery({
    queryKey: ['convites-recebidos'],
    enabled: user?.contexto !== 'PLATFORM',
    queryFn: () => api.get<{ data: { vencido: boolean }[] }>('/auth/convites'),
    staleTime: 5 * 60_000,
  });
  const convitesPendentes = (convites.data?.data ?? []).filter((c) => !c.vencido).length;

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (e: MouseEvent): void => {
      if (containerRef.current !== null && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    };
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const sair = async (): Promise<void> => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  // CERES-008 §5: troca de contexto sem logout. A sessão limpa o cache inteiro;
  // voltar ao painel evita ficar numa URL de registro que a organização nova
  // não alcança (o servidor responderia 404).
  const organizacoes = user?.organizations ?? [];
  const trocar = async (organizationId: string): Promise<void> => {
    setAberto(false);
    if (organizationId === user?.organizationId) return;
    await trocarOrganizacao(organizationId);
    navigate('/', { replace: true });
  };

  const item = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-ink hover:bg-surface-sunken';

  return (
    <div ref={containerRef} className="relative">
      {/* O acionador vive na sidebar Forest Green — texto claro; o menu aberto
          continua sendo uma superfície clara comum. */}
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className={
          colapsado
            ? 'flex h-10 w-10 items-center justify-center rounded-lg hover:bg-brand-700'
            : 'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-brand-700'
        }
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={`${(user?.name ?? '').replace(/\[DEMO\]\s*/, '')} · ${
          user?.partnerName ?? user?.organizationName ?? ''
        }`}
      >
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-300 text-2xs font-semibold text-brand-700"
          aria-hidden="true"
        >
          {iniciais(user?.name ?? '?')}
        </div>
        {!colapsado && (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink-inverse">
                {user?.name.replace(/\[DEMO\]\s*/, '')}
              </p>
              <p className="truncate text-2xs text-brand-300">
                {user?.partnerName ?? user?.organizationName}
                {user?.readOnly === true && ' · somente leitura'}
              </p>
            </div>
            <IconChevronDown className="h-4 w-4 shrink-0 text-brand-300" />
          </>
        )}
      </button>

      {aberto && (
        <div
          role="menu"
          className={`absolute bottom-full z-40 mb-1 min-w-[13rem] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-pop ${
            colapsado ? 'left-0 w-56' : 'left-0 w-full'
          }`}
        >
          <Link to="/meu-perfil" role="menuitem" className={item} onClick={() => setAberto(false)}>
            <IconUsers className="h-4 w-4 shrink-0 text-ink-subtle" />
            Meu perfil
          </Link>
          <Link to="/preferencias" role="menuitem" className={item} onClick={() => setAberto(false)}>
            <IconSettings className="h-4 w-4 shrink-0 text-ink-subtle" />
            Preferências
          </Link>

          {(user?.mfaEnabled === true || user?.mfaRequired === true) && (
            <Link to="/configurar-mfa" role="menuitem" className={item} onClick={() => setAberto(false)}>
              <IconShield className="h-4 w-4 shrink-0 text-ink-subtle" />
              Segundo fator
              {user?.mfaSetupRequired === true && (
                <span className="ml-auto badge-warning">pendente</span>
              )}
            </Link>
          )}

          {organizacoes.length > 1 && (
            <>
              <div className="my-1 border-t border-line" />
              <p className="px-3 py-1 text-2xs font-medium uppercase tracking-wide text-ink-subtle">
                {t('org.trocarDe')}
              </p>
              {organizacoes.map((organizacao) => (
                <button
                  key={organizacao.id}
                  type="button"
                  role="menuitem"
                  className={item}
                  onClick={() => void trocar(organizacao.id)}
                >
                  <IconBuilding className="h-4 w-4 shrink-0 text-ink-subtle" />
                  <span className="min-w-0 flex-1 truncate">{organizacao.name}</span>
                  {organizacao.id === user?.organizationId && (
                    <span className="badge-positive ml-auto">{t('org.atual')}</span>
                  )}
                </button>
              ))}
            </>
          )}

          {/* CERES-ACC-005-OPER-001: outra empresa com o mesmo login — só com o
              cadastro aberto e fora da administração da plataforma. */}
          {cadastroAberto && (user?.platformRole ?? null) === null && (
            <Link to="/nova-empresa" role="menuitem" className={item} onClick={() => setAberto(false)}>
              <IconBuilding className="h-4 w-4 shrink-0 text-ink-subtle" />
              {t('outraOrganizacao.link')}
            </Link>
          )}

          {/* CERES-ADM-002: só para quem tem papel de plataforma. Entrar troca
              o contexto da sessão — é explícito, e a tela diz para onde vai. */}
          {(user?.platformRole ?? null) !== null && (
            <>
              <div className="my-1 border-t border-line" />
              <button
                type="button"
                role="menuitem"
                className={item}
                onClick={() => {
                  setAberto(false);
                  void entrarNaPlataforma().then(() => navigate('/admin/plataforma', { replace: true }));
                }}
              >
                <IconShield className="h-4 w-4 shrink-0 text-ink-subtle" />
                {t('plat.titulo')}
              </button>
            </>
          )}

          {/* CERES-ADM-003: a administração DESTA organização, para quem cuida dela. */}
          {user?.partnerId === null && canAny(['user:manage', 'role:manage']) && (
            <>
              <div className="my-1 border-t border-line" />
              <Link to="/admin/organizacao" role="menuitem" className={item} onClick={() => setAberto(false)}>
                <IconUsers className="h-4 w-4 shrink-0 text-ink-subtle" />
                {t('org.adm.titulo')}
              </Link>
            </>
          )}
          {convitesPendentes > 0 && (
            <Link to="/convites" role="menuitem" className={item} onClick={() => setAberto(false)}>
              <IconBuilding className="h-4 w-4 shrink-0 text-ink-subtle" />
              {t('org.convites.titulo')}
              <span className="ml-auto badge-info">{convitesPendentes}</span>
            </Link>
          )}

          {podeAdministrar && (
            <>
              <div className="my-1 border-t border-line" />
              <Link to="/admin" role="menuitem" className={item} onClick={() => setAberto(false)}>
                <IconSettings className="h-4 w-4 shrink-0 text-ink-subtle" />
                Administração
              </Link>
            </>
          )}

          <div className="my-1 border-t border-line" />
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              setAberto(false);
              aoAbrirGuia();
            }}
          >
            <IconSpark className="h-4 w-4 shrink-0 text-ink-subtle" />
            Comece por aqui
          </button>

          <div className="my-1 border-t border-line" />
          <button type="button" role="menuitem" className={item} onClick={() => void sair()}>
            <IconLogout className="h-4 w-4 shrink-0 text-ink-subtle" />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}
